import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {LabelsSectionAssignedLabelsFragment} from '@github-ui/issue-metadata/LabelsSection'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {LabelsList} from '@github-ui/labels-list'
import {clsx} from 'clsx'
import {type ReactElement, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueMetadata$key} from './__generated__/IssueMetadata.graphql'
import {AssigneesMetadata} from './AssigneesMetadata'
import styles from './IssueMetadata.module.css'
import {MilestoneMetadata} from './MilestoneMetadata'

/*
  This fragment fetches metadata for the issue header, including assignees, labels, and milestones.
  It is primarily used for mobile views but is not conditionally accessed due to similar fragments in the IssueSidebar within the IssueViewer.
  Relay optimizes data fetching by retrieving the data only once in such cases.
  If this component is used outside the IssueViewer, this fragment should be conditionally included for mobile screens only.
*/
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueMetadataFragment = graphql`
  fragment IssueMetadata on Issue {
    labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
        }
      }
    }
    milestone {
      id
      title
      closed
      dueOn
      progressPercentage
      url
      closedAt
    }
    assignedActors(first: 20) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
    agentAssignments {
      assignee {
        id
        login
        avatarUrl(size: 64)
        isCopilot
        isAgent
        displayName
      }
      assigner {
        __typename
        ... on Node {
          id
        }
        ... on Actor {
          login
        }
      }
    }
  }
`

export const IssueMetadata = ({metadataKey}: {metadataKey: IssueMetadata$key}) => {
  const issueMetadata = useFragment(IssueMetadataFragment, metadataKey)
  // RELAY-CLEANUP(IssueMetadata): still-Relay parent data omits edge cursor/__typename fields expected by
  // IssueViewerViewQuery-derived types. Remove cast when IssueMetadata leaves Relay.
  const labelDataRaw = issueMetadata as LabelsSectionAssignedLabelsFragment

  const assigneesData = useMemo(() => {
    return ((issueMetadata?.assignedActors?.nodes || []).flatMap(assignee => {
      return assignee ? [assignee] : []
    }) ?? []) as AssigneePickerAssignee[]
  }, [issueMetadata?.assignedActors?.nodes])

  const labelsData = useMemo(() => {
    return (
      (labelDataRaw?.labels?.edges || []).flatMap(label => {
        return label?.node ? [label?.node] : []
      }) ?? []
    )
  }, [labelDataRaw?.labels?.edges])

  const milestoneData = issueMetadata?.milestone ?? null

  // return early if no data is available
  if (!assigneesData.length && !labelsData.length && !milestoneData) {
    return null
  }

  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')

  return (
    <div className={clsx(styles.issueMetadata, stickyHeaderEnabled && styles.stickyHeader)}>
      {assigneesData?.length > 0 && (
        <Metadata title="Assignees">
          <AssigneesMetadata assignees={assigneesData} />
        </Metadata>
      )}
      {labelsData?.length > 0 && (
        <Metadata title="Labels">
          <LabelsList labels={labelsData} />
        </Metadata>
      )}
      {milestoneData && (
        <Metadata title="Milestone">
          <MilestoneMetadata milestone={milestoneData} />
        </Metadata>
      )}
    </div>
  )
}

const Metadata = ({title, children}: {title: string; children: ReactElement}) => (
  <div className={styles.metadata}>
    <div className={styles.metadataTitle}>{title}</div>
    <div className={styles.metadataValue}>{children}</div>
  </div>
)
